import { http } from 'cdui-js';

export type AgreementAppClient = 'APP' | 'H5';

/** 内容类型：PDF / TEXT / HTML / RICH_TEXT（RICH_TEXT 时 fileUrl 为 HTML 正文） */
export type AgreementContentType = 'PDF' | 'TEXT' | 'HTML' | 'RICH_TEXT' | string;

export type AgreementSceneDocItem = {
  docCode: string;
  contentType: AgreementContentType;
  name: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  sortNo?: number;
  signed?: boolean;
  /** 后端若下发正文时可使用 */
  content?: string;
};

export type AgreementSceneBundle = {
  sceneCode: string;
  sceneName?: string;
  mandatory?: number;
  signMethod?: string;
  enabled?: boolean;
  docs: AgreementSceneDocItem[];
};

export type GetSceneBundleParams = {
  sceneCode: string;
  appClient?: AgreementAppClient;
  bizType?: string;
  bizId?: string;
};

/** 按场景拉取待签协议清单 GET /user/agreement-doc/scene/{sceneCode} */
export function fetchAgreementSceneBundle(params: GetSceneBundleParams) {
  const { sceneCode, appClient = 'H5', bizType, bizId } = params;
  const q = new URLSearchParams();
  // if (appClient) q.set('appClient', appClient);
  if (bizType) q.set('bizType', bizType);
  if (bizId) q.set('bizId', bizId);
  const qs = q.toString();
  return http.get(
    `/user/agreement-doc/scene/${encodeURIComponent(sceneCode)}${qs ? `?${qs}` : ''}`,
  ) as any;
}

export type AgreementSignItem = {
  docCode: string;
  /** JSON 字符串；按场景 signMethod：CHECK 用 checked/checkedAt，READ 用 readFinished，SIGNATURE 用 signatureFileKey */
  evidenceJson: string;
};

export type AgreementSignReq = {
  sceneCode: string;
  items: AgreementSignItem[];
  bizType?: string;
  bizId?: string;
  appClient?: AgreementAppClient;
};

export type AgreementSignBatchResponse = {
  signIds?: number[];
  signedDocCodes?: string[];
};

/** 提交协议签署 POST /user/agreement-doc/sign（按场景批量） */
export function signAgreementDoc(data: AgreementSignReq) {
  return http.post(`/user/agreement-doc/sign`, {
    ...data,
    appClient: data.appClient ?? 'H5',
  }) as any;
}

export type LoadAgreementDocsParams = {
  sceneCode: string;
  /** 需要绑定的协议编码，返回 docs 按此顺序 */
  docCodes: string[];
  appClient?: AgreementAppClient;
  bizType?: string;
  bizId?: string;
};

export type LoadAgreementDocsResult = {
  sceneCode: string;
  sceneName?: string;
  enabled?: boolean;
  /** 按传入 docCodes 顺序；未命中为 null */
  docs: (AgreementSceneDocItem | null)[];
  /** docCode → 协议项 */
  docMap: Record<string, AgreementSceneDocItem>;
};

/**
 * 按场景拉取协议清单，再按 docCodes 过滤后返回。
 * 业务页：传场景编码 + 协议码数组即可拿到要绑定的项。
 */
export async function loadAgreementDocsByCodes(
  params: LoadAgreementDocsParams,
): Promise<LoadAgreementDocsResult> {
  const { sceneCode, docCodes, appClient = 'H5', bizType, bizId } = params;

  const bundle = await fetchAgreementSceneBundle({
    sceneCode,
    // appClient,
    bizType,
    bizId,
  });

  const list = Array.isArray(bundle?.docs) ? bundle.docs : [];
  const docMap: Record<string, AgreementSceneDocItem> = {};
  for (const item of list) {
    if (item?.docCode) docMap[item.docCode] = item;
  }

  return {
    sceneCode: bundle?.sceneCode ?? sceneCode,
    sceneName: bundle?.sceneName,
    enabled: bundle?.enabled,
    docs: docCodes.map((code) => docMap[code] ?? null),
    docMap,
  };
}
